import { AlertTriangle, Euro, TrendingUp, Users } from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { cn } from "../../../client/utils";
import useTranslation from "../../../client/hooks/useTranslation";

interface Props {
  mrr: number;
  mrrMax: number;
  mrrPro: number;
  totalUsers: number;
  paymentIssueCount: number;
}

function formatEuros(amount: number): string {
  return `${Math.round(amount).toLocaleString()} €`;
}

const MrrOverviewCards = ({
  mrr,
  mrrMax,
  mrrPro,
  totalUsers,
  paymentIssueCount,
}: Props) => {
  const t = useTranslation();

  const tiles = [
    {
      key: "total",
      label: t.adminDashboard.mrrTotal,
      value: formatEuros(mrr),
      icon: Euro,
      accent: "text-primary",
    },
    {
      key: "max",
      label: t.adminDashboard.mrrMax,
      value: formatEuros(mrrMax),
      icon: TrendingUp,
      accent: "text-primary",
    },
    {
      key: "pro",
      label: t.adminDashboard.mrrPro,
      value: formatEuros(mrrPro),
      icon: TrendingUp,
      accent: "text-secondary",
    },
    {
      key: "users",
      label: t.adminDashboard.totalUsers,
      value: totalUsers.toLocaleString(),
      icon: Users,
      accent: "text-foreground",
    },
    {
      key: "issues",
      label: t.adminDashboard.paymentIssues,
      value: paymentIssueCount.toLocaleString(),
      icon: AlertTriangle,
      accent: paymentIssueCount > 0 ? "text-red-500" : "text-muted-foreground",
    },
  ];

  return (
    <div>
      <h2 className="text-foreground mb-1 text-lg font-semibold">
        {t.adminDashboard.mrrTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.mrrHint}
      </p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        {tiles.map(({ key, label, value, icon: Icon, accent }) => (
          <Card key={key}>
            <CardContent className="flex items-center gap-3 pt-4">
              <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-full">
                <Icon className={cn("size-4", accent)} />
              </div>
              <div className="min-w-0">
                <p className={cn("font-mono text-xl font-bold", accent)}>
                  {value}
                </p>
                <p className="text-muted-foreground truncate text-xs font-medium">
                  {label}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MrrOverviewCards;
